/**
 * «Surtido» — the words for the reason codes the plan writes are the PLATFORM's (`GET /assortment/reason-labels`, platform ADR 0057):
 * one list per place a code is written, each code with its label in the planner's language. This build carried a catalogue of its own
 * for them (ten maps, thirty-odd phrases, three locales) until the platform published its own; two sources of text for one thing is
 * how a screen and a file come to disagree, so the UI's copy is gone and only what is NOT a reason label remains in `./words.ts`.
 *
 * A code the platform does not label reads «Reason not described in this version» (ADR 9060) — never the code as text, never a blank.
 * While the labels are on their way nothing is drawn for a reason rather than its code.
 */

import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import type { ReasonLabels } from '@/api';

import { useReasonVocabulary } from './reads';

/** The places a reason code is written, as the platform names them. */
export const REASON_GROUP = {
  decision: 'decisionReason',
  line: 'lineReason',
  gap: 'gapReason',
  quantity: 'quantityReason',
  rule: 'targetRule',
  /** A row's missing reason, why the flags could not be judged (`baselineGap`), and a node the plan did not judge (`refusal`). */
  flag: 'flagReason',
  placement: 'placementReason',
  plan: 'planRefusal',
  /** An input the plan lacked — and why a peer basis is not offered. */
  absent: 'absentInput',
} as const;
export type ReasonGroup = (typeof REASON_GROUP)[keyof typeof REASON_GROUP];

/** Why a basis is not offered, as the bases read states it: the selling area is not held, or no scheme exists. */
export function basisReasonLabel(labels: ReasonLabels, reason: string): string | null {
  return labels.labelOrNull(REASON_GROUP.absent, reason) ?? labels.labelOrNull(REASON_GROUP.plan, `planning.assortment.${reason}`);
}

/**
 * A reason code in the planner's words: the platform's label; «Reason not described in this version» for a code it does not label;
 * `''` for no code, and while the labels have not arrived.
 */
export function useReasonWords(): (group: ReasonGroup, code: string | null | undefined) => string {
  const { t } = useTranslation();
  const labels = useReasonVocabulary();
  return useCallback((group, code) => {
    if (code === null || code === undefined || code === '' || !labels.loaded) return '';
    return labels.labelOrNull(group, code) ?? t('preseason.assortment.reasonUnknown');
  }, [labels, t]);
}

